import React, {useState, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {Clock} from '@styled-icons/fluentui-system-regular/Clock';

import {Label3, Label4, H4, Body4} from '@bootcamp/web/src/components/Typography/next';
import {Dismiss} from '@styled-icons/fluentui-system-filled/Dismiss';
import {ReactComponent as CaretDown} from '@bootcamp/shared/src/assets/svg/caret-down.svg';

import {Chiclet} from './shared';
import {typeDetails, subjectDetails} from '../CalendarDay';
import {InputComponent} from '@bootcamp/web/src/components/UI/inputs';

import CreateTest from '@bootcamp/web/src/bootcamps/pages/CreateTest';
import {RoundButton} from '@bootcamp/web/src/components/UI';
import DatePicker, {CalendarIconBareWrapper} from '../DatePicker';
import {useTooltipState} from '../../contexts/Tooltip';
import { formatEstimatedTime } from '../../helpers';

import {useUserDataContext} from '@bootcamp/web/src/contexts/UserData';
import {useModalContext} from '@bootcamp/web/src/contexts/Modal';



import {format, parse} from 'date-fns';
import moment from 'moment';
import {Checkmark} from '@styled-icons/fluentui-system-filled/Checkmark';
import {Delete} from '@styled-icons/fluentui-system-regular/Delete';
import {Copy} from '@styled-icons/fluentui-system-regular/Copy';
import nanoid from 'nanoid';

const FloatingModalContainer = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  ${({theme}) => theme.mediaQueries.tablet} {
    position: fixed;
    width: 100%;
    background: rgba(0, 0, 0, 0.5);
    z-index: 100000;
  }
`;
const FloatingModalWrapper = styled.div`
  position: absolute;
  z-index: 100000;
  background: white;
  visibility: hidden;
  box-shadow: ${({theme}) => theme.elevation.shadow.low};
  padding: 16px;
  opacity: 1;
  width: 284px;
  border-radius: 8px;
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  cursor: default;
  ${({theme}) => theme.mediaQueries.tablet} {
    width: 100%;
    bottom: 0;
    visibility: visible;
    left: 0;
    border-bottom-right-radius: 0;
    border-bottom-left-radius: 0;
    padding-bottom: 64px;
  }
`;
const AssignmentTopHeader = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  gap: 4px;
`;
const AssignmentModalHeader = styled.div`
`;
const AssignmentModalTitle = styled(H4)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  text-wrap: pretty;
  cursor: text;
`;

const CustomAssignmentModalTitle = styled(AssignmentModalTitle).attrs(props => ({as: 'input', autoFocus: true}))`
  border: none;
  outline: none;
`;

const CustomAssignmentEstimatedTime = styled.input.attrs(props => ({type: 'number', inputmode: 'numeric', pattern: "[0-9]*", placeholder: 'Estimated time', min: 0, max: 60*8, step: 15}))`
  border: none;
  outline: none;
  padding: 0;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-size: 14px;
  font-family: proxima-nova, sans-serif;
  background: transparent;
  width: 100%;
  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 16px;
  }
`;

const AssignmentModalDescription = styled(Body4)`
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  font-size: 14px;
  line-height: 150%;
  margin-top: 12px;
  cursor: text;
  p {
    margin: 0;
  }
`;

const AssignmentLinkButton = styled(RoundButton).attrs(props => ({full: true, small: true}))`
  margin-top: 12px;
`;

const AssignmentModalDetails = styled.div`
  display: flex;
  flex-direction: column;
  padding-top: 8px;
  border-collapse: collapse;
`;

const AssignmentModalDetailsRow = styled.div`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 36px;
  padding: 4px;
  gap: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  border-top: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  font-family: proxima-nova, sans-serif;
  font-size: 14px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  &:last-child {
    border-bottom: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
  }

  svg {
    z-index: 1;
    color: ${({theme}) => theme.colors.neutralsPalette.grey};
  }

  &:hover {
    background: ${({theme}) => theme.colors.backgroundPalette.secondary};

    ${CalendarIconBareWrapper} {
      background: ${({theme}) => theme.colors.neutralsPalette.light};
    }
  }
  ${({theme}) => theme.mediaQueries.tablet} {
    height: 42px;
  }
`;
const StyledClock = styled(Clock)`
  width: 14px;
  height: 14px;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  margin-right: 4px;
`;
const DeleteIcon = styled(Delete)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
  cursor: pointer;
  margin-left: auto;
  margin-right: 16px;
`;
const CopyIcon = styled(Copy)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  cursor: pointer;
  margin-right: 16px;
`;

const CloseIcon = styled(Dismiss)`
  width: 16px;
  height: 16px;
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  cursor: pointer;
  ${({solo}) => solo && `
    margin-left: auto;
  `}
`;
const StyledChiclet = styled(Chiclet)`
  padding: 4px 8px;
`;
const DurationChiclet = styled(StyledChiclet)`
  background: white;
  color: ${({theme}) => theme.colors.typographyPalette.secondary};
  border: 1px solid ${({theme}) => theme.colors.borderPalette.primary};
`;
const DetailRowText = styled(Label3)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
  font-weight: 400;
  ${({theme}) => theme.mediaQueries.tablet} {
    font-size: 16px;
  }
`;

const Indicator = styled(CaretDown)`
  position: absolute;
  width: 24px;
  height: 24px;
  bottom: -16px;
  left: calc(50% - 12px);
`;

const CheckboxWrapper = styled.div`
  padding: 3px;
`;

const Checkbox = styled(InputComponent)`
  svg {
    color: white;
  }
`;
const SubjectText = styled(Label4)`
  font-size: 14px;
  color: ${({theme, palette}) => !palette ? '#85868E' : theme.colors.assignmentColorPalette[palette]?.dark};
  margin-top: 8px;
`;

const FloatingModal = ({children, onCloseCallback}) => {
  const {tooltipId, setTooltipId} = useTooltipState();
  const [modalStyle, setModalStyle] = useState({});
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const modalRef = useRef();
  const parentRef = useRef();

  const calculatePosition = () => {
    const SPACING = 10;
    if (!document.getElementById("calendar-container")) {
      return;
    };

    if (parentRef.current && modalRef.current) {
      const parentRect = parentRef.current.getBoundingClientRect();
      const modalWidth = modalRef.current.offsetWidth;
      const modalHeight = modalRef.current.offsetHeight;

      const calendarContainer = document.getElementById("calendar-container");
      const calendarRect = calendarContainer.getBoundingClientRect();

      let modalStyleUpdate = {};
      let indicatorStyleUpdate = {};

      // Adjusted positions relative to calendar-container bounds
      const topSpace = parentRect.top - Math.max(72, calendarRect.top); // Space above parent

      // Try positioning above the parent
      if (topSpace >= modalHeight + SPACING) {
          modalStyleUpdate.bottom = `calc(100% + ${SPACING}px)`;
          modalStyleUpdate.left = `-${(modalWidth - parentRect.width) / 2}px`;
      }
      // If no space above, position below
      else {
        modalStyleUpdate.top = `calc(100% + ${SPACING}px)`;
        modalStyleUpdate.left = `-${(modalWidth - parentRect.width) / 2}px`;

        indicatorStyleUpdate.transform =  "rotate(180deg)";
        indicatorStyleUpdate.top =  "-16px";
      }

      // left / right positioning
      if ((parentRect.right + ((modalWidth - parentRect.width) / 2)) > calendarRect.right) {
        modalStyleUpdate.left = "auto";
        modalStyleUpdate.right = 0; // Align modal's right edge with parent's right edge

        indicatorStyleUpdate.left = "auto";
        indicatorStyleUpdate.right = (parentRect.width / 2) - 12;
      } else if ((parentRect.left - ((modalWidth - parentRect.width) / 2)) < calendarRect.left) {
        modalStyleUpdate.left = 0; // Align modal's left edge with the container edge
        modalStyleUpdate.right = "auto";

        indicatorStyleUpdate.right = "auto";
        indicatorStyleUpdate.left = (parentRect.width / 2) - 12;
      }


      modalStyleUpdate.visibility = "visible";

      setModalStyle(modalStyleUpdate);
      setIndicatorStyle(indicatorStyleUpdate);
    }
  };

  const handleResize = () => setTooltipId(null);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setTooltipId(null);
      onCloseCallback && onCloseCallback();
    }
  };

  const handleContainerScroll = () => {
    // placeholder for sticky modal
    calculatePosition();
  };

  useEffect(() => {
    const pageContainer = document.getElementById("page-container");

    calculatePosition();

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousedown", handleClickOutside);

    pageContainer.addEventListener("scroll", handleContainerScroll);

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        handleClickOutside(event);
      }
    };
    window.addEventListener("keydown", handleEscape);
    if (document.documentElement.clientWidth < 768) {
      pageContainer.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("keydown", handleEscape);
      setTooltipId(null);
      pageContainer.removeEventListener("scroll", handleContainerScroll);
      pageContainer.style.overflow = "auto";
    };
  }, []);

  useEffect(() => {
    if (parentRef?.current && !['examDate'].includes(tooltipId)) {
      parentRef?.current?.focus();
    }
  }, [])

  return (
    <FloatingModalContainer key={tooltipId} ref={parentRef} tabIndex={0}>
      <FloatingModalWrapper ref={modalRef} style={modalStyle}>
        {children}
        <Indicator style={indicatorStyle}/>
      </FloatingModalWrapper>
    </FloatingModalContainer>
  )
}

const SubjectSquare = styled.div`
  width: 20px;
  height: 20px;
  background-color: ${({theme, color}) => theme.colors.assignmentColorPalette[color]?.light};
  border: 1px solid ${({theme, color}) => theme.colors.assignmentColorPalette[color]?.dark}1a;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SubjectSquareContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
`;


export const CustomAssignmentModal = ({date, startDate, examDate, event, onInputClick, checked, updateEvent, updateAssignmentDate, createAssignmentOnDate}) => {
  const titleRef = useRef();
  const {setTooltipId} = useTooltipState();
  const [title, setTitle] = useState(event.title);
  const [estimatedTime, setEstimatedTime] = useState(event.estimatedTime);
  const [type, setType] = useState(event.type);
  const [complete, setComplete] = useState(event.complete);
  const {searchUserInteractions, bootcamp} = useUserDataContext();

  useEffect(() => {
    updateEvent(event.id, date, {...event, title, estimatedTime, type, complete}, false);
  }, [title, estimatedTime, type, complete]);

  const handleSave = async () => {
    await updateEvent(event.id, date, {...event, title, estimatedTime, type, complete}, true);
    setTooltipId(null);
  }
  const formattedDate = new Date(date).toISOString().split('T')[0];

  const handleDateChange = (updatedDate) => {
    if (updatedDate === formattedDate) return;
    updateAssignmentDate(event.id, formattedDate, updatedDate);
  };
  useEffect(() => {
    if (titleRef?.current) {
      setTimeout(() => titleRef.current?.focus(), 1);
    }
  }, []);
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSave();
    }
  };

  useEffect(() => {
    window.addEventListener('keypress', handleKeyPress);
    return () => {
      window.removeEventListener('keypress', handleKeyPress);
    };
  }, [handleKeyPress]);
  const useSubjectColors = searchUserInteractions('useSubjectColors', false) && ['inbde', 'dat', 'oat', 'med-school'].includes(bootcamp);
  const ChicletType = useSubjectColors ? DurationChiclet : StyledChiclet;
  const palette = typeDetails[event.type].color;
  const testDate = searchUserInteractions('testDate');

  const duplicateEvent = (event) => {
    const newEvent = {...event, id: nanoid(), complete: false};
    createAssignmentOnDate(formattedDate, newEvent, true);
    setTooltipId(null);
  }

  return (
    <FloatingModal onCloseCallback={handleSave}>
      <AssignmentTopHeader>
        <ChicletType color={palette}>Custom</ChicletType>
        {event.estimatedTime > 0 ? <DurationChiclet>
          <StyledClock />
          {formatEstimatedTime(event.estimatedTime)}
        </DurationChiclet> : null}
        <DeleteIcon color="#E05E57" size={16} onClick={() => updateEvent(event.id, date, null, true)} />
        <CopyIcon size={16} onClick={e => duplicateEvent(event)} />
        <CloseIcon onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTooltipId(null);
        }} />
      </AssignmentTopHeader>
      <AssignmentModalHeader>
        <CustomAssignmentModalTitle ref={titleRef} value={title} autoFocus placeholder="New assignment" onChange={e => setTitle(e.target.value)}/>
      </AssignmentModalHeader>
      <AssignmentModalDetails tabIndex={-1}>
        <AssignmentModalDetailsRow>
          <SubjectSquareContainer>
            {Object.keys(typeDetails).map((typeDetail) => (
              <SubjectSquare color={typeDetails[typeDetail].color} onClick={() => setType(typeDetail)}>
                {typeDetail === type && <Checkmark color="#2f3037" size={12} />}
              </SubjectSquare>
            ))}
          </SubjectSquareContainer>
        </AssignmentModalDetailsRow>
        <AssignmentModalDetailsRow>
          <StyledClock style={{marginRight: 0, height: '20px', width: '20px', minWidth: '20px'}} />
          <CustomAssignmentEstimatedTime
            value={estimatedTime}
            onChange={(e) => e.target.value < 0 || e.target.value > 60*8 ? false : setEstimatedTime(e.target.value.replace('e', ''))}
          />
          <span style={{position: 'absolute', right: '10%', zIndex: '-1'}}>minutes</span>
        </AssignmentModalDetailsRow>
        <AssignmentModalDetailsRow>
          <DatePicker
            type={'inline'}
            value={formattedDate}
            startDate={startDate}
            min={startDate}
            endDate={testDate}
            onChange={handleDateChange}
            containerId={'calendar-container'}
          />
        </AssignmentModalDetailsRow>
        <AssignmentModalDetailsRow onClick={() => setComplete(!complete)}>
          <CheckboxWrapper>
            <Checkbox
              size={14}
              checked={complete}
              onClick={() => setComplete(!complete)}
            />
          </CheckboxWrapper>
          <DetailRowText tabIndex={-1}>Complete</DetailRowText>
        </AssignmentModalDetailsRow>
      </AssignmentModalDetails>
      <AssignmentLinkButton onClick={handleSave}>
        Save
      </AssignmentLinkButton>
    </FloatingModal>
  )
}

const DeleteAssignmentText = styled(Label3)`
  color: ${({theme}) => theme.colors.interfacePalette.red.default};
  cursor: pointer;
`;

function getDefaultsFromURL(eventUrl, bootcamp) {
  try {
    const url = new URL(eventUrl);
    const params = new URLSearchParams(url.search);

    let defaults = {
      masteryLevelFallback: 'all',
    };

    const tutorMode = params.get('tutorMode');
    const timed = params.get('timed');
    const masteryLevel = params.get('masteryLevel');
    const questionCount = params.get('questionCount');
    const subjects = params.get('subjects');
    const type = params.get('type');
    const subject = params.get('subject');
    const masteryLevelFallback = params.get('masteryLevelFallback');

    if (tutorMode) defaults.tutorMode = tutorMode === 'true';
    if (timed) defaults.timed = timed === 'true';
    if (masteryLevel && ['all', 'untagged', 'learning', 'reviewing', 'mastered', 'bookmarked'].includes(masteryLevel)) defaults.masteryLevel = masteryLevel;
    if (questionCount && parseInt(questionCount) > 0 && parseInt(questionCount) <= 120) defaults.questionCount = parseInt(questionCount);
    if (subjects && typeof subjects === 'string') defaults.subjects = subjects?.split(',').map(subject => subject.trim());
    if (subject) defaults.subject = subject;
    if (type && bootcamp === 'med-school' && ['preclinical', 'bites', 'anatomy'].includes(type)) {
      defaults.type = type;
    }
    if (masteryLevelFallback && ['all', 'untagged', 'learning', 'reviewing', 'mastered', 'bookmarked'].includes(masteryLevelFallback)) defaults.masteryLevelFallback = masteryLevelFallback;
    return defaults;
  } catch (error) {
    return {};
  }
}
function getDefaultsFromTitle(eventTitle) {
  try {
    const parts = eventTitle.split(' ');

    let defaults = {};

    const count = parts.find(part => part.match(/\d+/));
    const masteryLevel = parts.find(part => ['learning', 'reviewing'].includes(part.toLowerCase()));

    if (count) defaults.questionCount = parseInt(count);
    if (masteryLevel) defaults.masteryLevel = masteryLevel.toLowerCase();

    return defaults;
  } catch (error) {
    return {}
  }
}
function getCreateTestDefaults(event, bootcamp) {
  const {externalLink, title} = event;

  const defaults = externalLink?.match('create-test')
    ? getDefaultsFromURL(externalLink, bootcamp)
    : getDefaultsFromTitle(title);

  return defaults;
}

const AssignmentModal = ({ date, startDate, event, updateCompleteStatus, onInputClick, checked, updateAssignmentDate}) => {
  const {searchUserInteractions, bootcamp} = useUserDataContext();
  const {setTooltipId} = useTooltipState();

  const testDate = searchUserInteractions('testDate');
  const formattedDate = new Date(date).toISOString().split('T')[0];

  const {modalDispatch} = useModalContext();

  const handleDateChange = (date) => {
    if (date === formattedDate) return;
    updateAssignmentDate(event.id, formattedDate, date);
  };

  const useSubjectColors = searchUserInteractions('useSubjectColors', false) && ['inbde', 'dat', 'oat', 'med-school'].includes(bootcamp);
  const ChicletType = useSubjectColors ? DurationChiclet : StyledChiclet;
  const palette = useSubjectColors ? subjectDetails[bootcamp][event.subject]?.color : '';

  const studySpeed = searchUserInteractions('studySpeed', 1);
  const estimatedTime = parseInt(event.estimatedTime || 0) * studySpeed;


  function handleCreateTestClick() {
    try {
      const defaults = getCreateTestDefaults(event, bootcamp);

      modalDispatch({
        type: 'open',
        component: CreateTest,
        componentProps: {defaults},
        enableClickClose: true,
      });
    } catch (error) {
      // console.log('error opening create test', error);
    }
  }

  const buttonProps = event.type === 'createTest'
    ? {onClick: handleCreateTestClick, children: 'Create Test'}
    : {href: event.externalLink, children: 'Go to Assignment'};

  return (
    <FloatingModal>
      <AssignmentTopHeader>
        <ChicletType color={typeDetails[event.type].color}>{bootcamp === 'nclex' && event.type === 'practiceTest' ? 'Readiness Exam' : typeDetails[event.type].label}</ChicletType>
        {estimatedTime > 0 ? <DurationChiclet>
          <StyledClock />
          {formatEstimatedTime(estimatedTime)}
        </DurationChiclet> : null}
        <CloseIcon solo onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setTooltipId(null);
        }} />
      </AssignmentTopHeader>
      <AssignmentModalHeader>
        <AssignmentModalTitle>{event.title}</AssignmentModalTitle>
        {event?.subject && <SubjectText palette={palette}>{event.subject}</SubjectText>}
        {event?.description &&<AssignmentModalDescription dangerouslySetInnerHTML={{ __html: event.description }} />}
      </AssignmentModalHeader>
      <AssignmentModalDetails tabIndex={-1}>
        <AssignmentModalDetailsRow>
          <DatePicker
            type={'inline'}
            value={formattedDate}
            startDate={startDate}
            min={startDate}
            endDate={testDate}
            onChange={handleDateChange}
            containerId={'calendar-container'}
            />
        </AssignmentModalDetailsRow>
        <AssignmentModalDetailsRow onClick={onInputClick}>
          <CheckboxWrapper>
            <Checkbox
              size={14}
              checked={checked}
              onClick={onInputClick}
            />
          </CheckboxWrapper>
          <DetailRowText tabIndex={-1}>Complete</DetailRowText>
        </AssignmentModalDetailsRow>
      </AssignmentModalDetails>
      <AssignmentLinkButton {...buttonProps}/>
    </FloatingModal>
  );
};

export default AssignmentModal;

export const ExamDateModal = () => {
  const {DEFAULT_USER_ID, bootcamp, saveUserInteraction, searchUserInteractions} = useUserDataContext();
  const examDate = searchUserInteractions(`testDate`, false);
  const today = moment().format('YYYY-MM-DD');

  return (
    <FloatingModal>
      <AssignmentModalHeader>
        <AssignmentModalTitle>Exam Date</AssignmentModalTitle>
        <AssignmentModalDescription>Your exam date is {moment(examDate).format('MMM D, YYYY')}. You can change your exam date at any time below:</AssignmentModalDescription>
      </AssignmentModalHeader>
      <AssignmentModalDetails tabIndex={-1}>
        <AssignmentModalDetailsRow>
          <DatePicker
            min={today}
            startDate={examDate}
            type={'inline'}
            value={examDate}
            onChange={(date) => saveUserInteraction('testDate', JSON.stringify(date))}
            containerId={'calendar-container'}
          />
        </AssignmentModalDetailsRow>
      </AssignmentModalDetails>
    </FloatingModal>
  )

}
export const ExpirationDateModal = ({membershipExpirationDate}) => {
  const {DEFAULT_USER_ID, bootcamp, saveUserInteraction, searchUserInteractions} = useUserDataContext();
  const today = moment().format('YYYY-MM-DD');
  const formattedExpirationDate = moment(membershipExpirationDate).format('YYYY-MM-DD');
  return (
    <FloatingModal>
      <AssignmentModalHeader>
        <AssignmentModalTitle>Membership Ends</AssignmentModalTitle>
        <AssignmentModalDescription>Your Bootcamp membership is ending on {moment(membershipExpirationDate).format('MMM D, YYYY')}. You can extend your membership anytime by going to your Account:</AssignmentModalDescription>
      </AssignmentModalHeader>
      <AssignmentModalDetails tabIndex={-1}>
        <AssignmentModalDetailsRow>
          <DatePicker
            min={today}
            startDate={formattedExpirationDate}
            type={'inline'}
            value={formattedExpirationDate}
            onChange={(date) => false}
            containerId={'calendar-container'}
            disabled
          />
        </AssignmentModalDetailsRow>
      </AssignmentModalDetails>
      <AssignmentLinkButton href={`/${bootcamp}/account/memberships`} target="_blank">
        Go to Account
      </AssignmentLinkButton>
    </FloatingModal>
  )

}